/**
 * useTableData Hook
 * Combined hook for search, filter, sort, and pagination
 *
 * Usage:
 * const {
 *     filteredItems,
 *     paginatedItems,
 *     search,
 *     setSearch,
 *     sortBy,
 *     setSortBy,
 *     pagination,
 * } = useTableData(items, {
 *     searchFields: ['first_name', 'last_name', 'email'],
 *     defaultSort: { field: 'created_at', direction: 'desc' },
 *     perPage: 10,
 * });
 */

import { useState, useMemo, useCallback } from 'react';
import { usePagination } from './usePagination';
import { DEFAULT_ITEMS_PER_PAGE } from '@/config/constants';

export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
    field: string;
    direction: SortDirection;
}

export interface UseTableDataOptions<T> {
    /** Fields to search in */
    searchFields?: (keyof T)[];
    /** Custom search function */
    searchFn?: (item: T, term: string) => boolean;
    /** Default sort configuration */
    defaultSort?: SortConfig;
    /** Custom sort function */
    sortFn?: (a: T, b: T, sortBy: SortConfig) => number;
    /** Items per page */
    perPage?: number;
    /** Custom filter function (in addition to search) */
    filterFn?: (item: T) => boolean;
}

export interface UseTableDataReturn<T> {
    // Search
    search: string;
    setSearch: (value: string) => void;
    clearSearch: () => void;

    // Sort
    sortBy: SortConfig;
    setSortBy: (config: SortConfig) => void;
    toggleSort: (field: string) => void;

    // Results
    filteredItems: T[];
    paginatedItems: T[];
    totalFiltered: number;

    // Pagination
    pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        perPage: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
        goToPage: (page: number) => void;
        nextPage: () => void;
        prevPage: () => void;
        reset: () => void;
    };

    // Helpers
    isEmpty: boolean;
    isSearching: boolean;
    resetAll: () => void;
}

export function useTableData<T>(
    items: T[],
    options: UseTableDataOptions<T> = {}
): UseTableDataReturn<T> {
    const {
        searchFields = [],
        searchFn,
        defaultSort = { field: 'created_at', direction: 'desc' as SortDirection },
        sortFn,
        perPage = DEFAULT_ITEMS_PER_PAGE,
        filterFn,
    } = options;

    const [search, setSearchValue] = useState('');
    const [sortBy, setSortBy] = useState<SortConfig>(defaultSort);

    // Search handler with trimming
    const setSearch = useCallback((value: string) => {
        setSearchValue(value);
    }, []);

    const clearSearch = useCallback(() => {
        setSearchValue('');
    }, []);

    // Sort toggle helper
    const toggleSort = useCallback((field: string) => {
        setSortBy(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    }, []);

    // Filter and sort items
    const filteredItems = useMemo(() => {
        let result = [...items];

        // Apply custom filter
        if (filterFn) {
            result = result.filter(filterFn);
        }

        // Apply search
        if (search.trim()) {
            const term = search.toLowerCase().trim();

            if (searchFn) {
                result = result.filter(item => searchFn(item, term));
            } else if (searchFields.length > 0) {
                result = result.filter(item => {
                    return searchFields.some(field => {
                        const value = item[field];
                        if (typeof value === 'string') {
                            return value.toLowerCase().includes(term);
                        }
                        if (typeof value === 'number') {
                            return String(value).includes(term);
                        }
                        return false;
                    });
                });
            }
        }

        // Apply sort
        if (sortFn) {
            result.sort((a, b) => sortFn(a, b, sortBy));
        } else {
            result.sort((a, b) => {
                const aVal = a[sortBy.field as keyof T];
                const bVal = b[sortBy.field as keyof T];

                // Handle dates
                if (sortBy.field.includes('_at') || sortBy.field.includes('date')) {
                    const aDate = new Date(aVal as string).getTime() || 0;
                    const bDate = new Date(bVal as string).getTime() || 0;
                    return sortBy.direction === 'asc' ? aDate - bDate : bDate - aDate;
                }

                // Handle strings
                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    const comparison = aVal.localeCompare(bVal);
                    return sortBy.direction === 'asc' ? comparison : -comparison;
                }

                // Handle numbers
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortBy.direction === 'asc' ? aVal - bVal : bVal - aVal;
                }

                return 0;
            });
        }

        return result;
    }, [items, search, sortBy, searchFields, searchFn, sortFn, filterFn]);

    // Apply pagination
    const { paginatedItems, pagination: paginationState } = usePagination(filteredItems, { perPage });

    // Reset all state
    const resetAll = useCallback(() => {
        setSearchValue('');
        setSortBy(defaultSort);
        paginationState.reset();
    }, [defaultSort, paginationState]);

    return {
        // Search
        search,
        setSearch,
        clearSearch,

        // Sort
        sortBy,
        setSortBy,
        toggleSort,

        // Results
        filteredItems,
        paginatedItems,
        totalFiltered: filteredItems.length,

        // Pagination
        pagination: paginationState,

        // Helpers
        isEmpty: paginatedItems.length === 0,
        isSearching: search.trim().length > 0,
        resetAll,
    };
}

export default useTableData;
